import { useState, useMemo } from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
// import { QueryClient } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Table,
  TableBody,
  // TableCaption,
  TableCell,
  // TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
// import { YearSelector } from "@/components/YearSelector";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
// import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import Pagination from "@/components/Pagination";

// icons
import { CirclePlus, Eye } from "lucide-react";

// Services
import { getFinance } from "@/services/finance";
import { formatNumber } from "@/utils/formatNumber";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
import { ErrorResponse } from "@/types/ErrorResponse";

interface FinanceReportItem {
  id: string;
  yearly: number;
  registeredCapital: number;
  totalAsset: number;
  totalDebt: number;
  initial: number;
  operating: number;
  netOperating: number;
  annualNet: number;
  cashYearly: number;
}

interface FinanceReportResponse {
  status: boolean;
  message: string;
  total: number;
  data: FinanceReportItem[];
}

export const Route = createFileRoute("/_layout-private/report/finance/")({
  component: () => <ReportFinance />,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
      startYear: Number(search.startYear) || 0,
      endYear: Number(search.endYear) || 0,
    };
  },
});

function ReportFinance() {
  const {
    page,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();
  const navigate = useNavigate();
  const companyId = JSON.parse(localStorage.getItem("companyId") || "null");
  const initialZero = 0;
  const currentYear = new Date().getFullYear();
  const { company_financial_report } = useAccessPermission();

  const [filter, setFilter] = useState({
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!filter.startYear || filter.startYear === initialZero) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === filter.startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [filter.startYear, yearOptions]);

  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const { data, isLoading, error } = useQuery<
    FinanceReportResponse,
    AxiosError<ErrorResponse>
  >({
    queryKey: ["finance", companyId, filter.startYear, filter.endYear, page],
    queryFn: () =>
      getFinance({
        companyId,
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
        skip: (page - 1) * 40,
        limit: 40,
      }),
  });

  if (error) {
    if (error?.response?.data?.message === "LOGGED_OUT") {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Functions
  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };
  return company_financial_report.isRead ? (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານການເງິນຂອງບໍລິສັດ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານການເງິນຂອງບໍລິສັດ</CardTitle>
          <CardDescription>ລາຍການລາຍງານການເງິນຂອງບໍລິສັດ</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between w-full mt-5 mb-5">
            <div className="flex items-center gap-x-2">
              <div>
                <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
                <Select
                  value={filter.startYear.toString()}
                  onValueChange={handleStartYearChange}
                >
                  <SelectTrigger className="w-[150px] indent-1">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">ທັງໝົດ</SelectItem>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="">ປີສິ້ນສຸດ</Label>
                <Select
                  value={filter.endYear.toString()}
                  onValueChange={handleEndYearChange}
                  disabled={
                    !filter.startYear || filter.startYear === initialZero
                  }
                >
                  <SelectTrigger className="w-[150px] indent-1">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {endYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              {company_financial_report.isWrite && (
                <Button
                  className="flex items-center gap-2"
                  onClick={() => {
                    localStorage.setItem("year", JSON.stringify(""));
                    navigate({
                      to: "/report/finance/company/finance-property/add",
                    });
                  }}
                >
                  <CirclePlus size={16} /> ເພີ່ມລາຍງານ
                </Button>
              )}
            </div>
          </div>
          <div className="w-full overflow-x-auto border rounded-sm">
            <div>
              {isLoading ? (
                <DinamixSkeletonTable rows={5} columns={8} />
              ) : (
                <Table className="w-[180%] mb-4">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-auto">ລໍາດັບ</TableHead>
                      <TableHead className="w-auto">ປະຈຳປີ</TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ທຶນຈົດທະບຽນ <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ລວມຊັບສິນທັງໝົດ <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ລວມໜີ້ສິນທັງໝົດ <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ຜົນໄດ້ຮັບເບື້ອງຕົ້ນ{" "}
                          <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ຜົນໄດ້ຮັບໃນທຸລະກິດ{" "}
                          <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ຜົນໄດ້ຮັບສຸທິຈາກການດຳເນີນງານ{" "}
                          <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ຜົນໄດ້ຮັບສຸທິໃນປີ{" "}
                          <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-x-1">
                          ສ່ວນປ່ຽນແປງເງິນສົດລວມ{" "}
                          <p className="text-gray-300">(ກີບ)</p>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.map(
                      (invoice: FinanceReportItem, index: number) => (
                        <TableRow
                          onClick={() => {
                            localStorage.setItem(
                              "year",
                              JSON.stringify(invoice?.yearly)
                            );
                            navigate({
                              to: "/report/finance/company/finance-property",
                            });
                          }}
                          key={invoice?.id}
                          className="cursor-pointer"
                        >
                          <TableCell className="font-medium text-center">
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex text-center gap-x-2">
                              <Eye size={16} color="#007AFF" />{" "}
                              {invoice?.yearly}
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.registeredCapital) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.totalAsset) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.totalDebt) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.initial) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.operating) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.netOperating) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.annualNet) || "-"}
                          </TableCell>
                          <TableCell>
                            {formatNumber(invoice?.cashYearly) || "-"}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <Pagination
            handlePageClick={handlePageClick}
            totalItems={data?.total || 0}
            itemsPerPage={40}
            currentPage={page}
          />
        </CardContent>
      </Card>
    </section>
  ) : (
    <NotPermission />
  );
}
